import axios from 'axios';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';

/**
 * Componente de Login
 * AIDEV-NOTE: Primeira tela do fluxo de autenticação
 */
export function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, senha);
      navigate('/');
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        // AIDEV-NOTE: server e rate limiters retornam o texto em `data.error`; `message` so no 500.
        // Ler ambos evita esconder o motivo real (ex.: 429) atras do texto generico.
        const serverMsg = err.response?.data?.error ?? err.response?.data?.message;
        if (status === 401 || status === 403) {
          setError('E-mail ou senha incorretos');
        } else if (status === 429) {
          setError(serverMsg ?? 'Muitas tentativas. Aguarde alguns minutos e tente novamente.');
        } else if (!err.response) {
          // Timeout ou sem conectividade — nenhuma resposta do servidor
          setError('Sem resposta do servidor. Verifique sua conexão e tente novamente.');
        } else {
          setError(serverMsg ?? 'Falha na autenticação. Tente novamente.');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Falha na autenticação');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              AzoupVendas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistema de Gestão de Vendas
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              disabled={isLoading}
            />

            <TextField
              fullWidth
              label="Senha"
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              disabled={isLoading}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Entrar'}
            </Button>
          </form>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Use suas credenciais do sistema
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
}
